import {
  Box,
  Grid,
  Card,
  Container,
  Typography,
  CardContent,
} from "@mui/material";
import React, { useRef, useEffect, useState } from "react";
import AUTOMOBILE from "../../assets/icons/BecomePartner/Automobile.png";
import CARE from "../../assets/icons/BecomePartner/Care.png";
import CYBERPROTECTION from "../../assets/icons/BecomePartner/CyberProtection.png";
import DIGITALBENEFITS from "../../assets/icons/BecomePartner/DigitalBenefits.png";
import GADGETS from "../../assets/icons/BecomePartner/Gadgets.png";
import VOYAGE from "../../assets/icons/BecomePartner/Voyage.png";

// Carefully edited to 2 lines, similar length and tone
const BENEFITS_LIST = [
  {
    icon: AUTOMOBILE,
    title: "Across Automobile",
    descp:
      "Instant help for private or commercial vehicles—roadside, towing, and breakdown assistance.",
  },
  {
    icon: GADGETS,
    title: "Across Gadgets",
    descp:
      "Total protection for mobiles, gadgets, and home appliances—covers damage, breakdown, and repairs.",
  },
  {
    icon: VOYAGE,
    title: "Across Voyage",
    descp:
      "24x7 travel and medical support anywhere—trip assistance, emergencies, and healthcare abroad.",
  },
  {
    icon: CARE,
    title: "Across Care",
    descp:
      "Health and wellness for your customers—medical teleconsultation, wellness programs, and support.",
  },
  {
    icon: CYBERPROTECTION,
    title: "Across Web",
    descp:
      "Shield against cyber risks—digital security, identity theft, and online fraud protection.",
  },
  {
    icon: DIGITALBENEFITS,
    title: "Across Digi",
    descp:
      "Exclusive digital benefits—shopping, lifestyle deals, service add-ons, and more.",
  },
];

// Tilt card effect hook
function useTilt(ref, isHovered) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    function handleMouseMove(e) {
      if (!isHovered) return;
      const rect = node.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const px = (x / rect.width - 0.5) * 2;
      const py = (y / rect.height - 0.5) * 2;
      const rotateX = -py * 12;
      const rotateY = px * 12;
      node.style.transform = `perspective(700px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.03)`;
      node.style.zIndex = 2;
      node.style.boxShadow = `0 16px 36px 0 #dfc4b6bb, 0 2px 12px #e5dedacc`;
    }
    function handleMouseLeave() {
      node.style.transform =
        "perspective(700px) rotateX(0deg) rotateY(0deg) scale(1)";
      node.style.zIndex = 1;
      node.style.boxShadow = "0 0px 0px 0 transparent";
    }
    node.addEventListener("mousemove", handleMouseMove);
    node.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      node.removeEventListener("mousemove", handleMouseMove);
      node.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [ref, isHovered]);
}

// TiltCard component
function TiltCard({ item, visible, animateDelay = 0 }) {
  const tiltRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useTilt(tiltRef, isHovered);

  useEffect(() => {
    const node = tiltRef.current;
    if (!node) return;
    if (!isHovered) {
      node.style.boxShadow = "none";
      node.style.transform =
        "perspective(700px) rotateX(0deg) rotateY(0deg) scale(1)";
      node.style.zIndex = 1;
    }
  }, [isHovered]);

  // Animate when visible using fade/slide (CSS inline here)
  return (
    <Card
      ref={tiltRef}
      sx={{
        ...styles.BenefitsCardContainer,
        opacity: visible ? 1 : 0,
        transform: visible
          ? "translateY(0)"
          : "translateY(60px)",
        transition: `opacity 0.6s cubic-bezier(0.23,1,0.32,1) ${animateDelay}ms, transform 0.6s cubic-bezier(0.23,1,0.32,1) ${animateDelay}ms, box-shadow 0.18s, transform 0.18s`,
        pointerEvents: visible ? "auto" : "none",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1,
          }}
        >
          <img
            src={item.icon}
            alt="benefits"
            style={styles.IconContainer}
          />
          <Typography
            variant="h3"
            sx={{
              fontSize: {
                xs: "1rem",
                sm: "1.12rem",
                md: "1.18rem",
                lg: "1.298rem",
              },
            }}
          >
            {item.title}
          </Typography>
        </Box>
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              minHeight: "2.7em", // Ensures height for 2 lines for consistency
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              fontSize: {
                xs: "0.97rem",
                sm: "1.04rem",
                md: "1.07rem",
                lg: "1.10rem",
              },
            }}
          >
            {item.descp}
          </Typography>
        </CardContent>
      </Box>
    </Card>
  );
}

// Reveal cards one by one when section is visible
const DiscoveryCategory = () => {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(false);
  const [showCards, setShowCards] = useState(0);

  // Section visibility detection (Intersection Observer)
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    let observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
        }
      },
      {
        threshold: 0.22,
      }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Animate cards in one-by-one when active
  useEffect(() => {
    let timer;
    if (active && showCards < BENEFITS_LIST.length) {
      timer = setTimeout(() => {
        setShowCards((n) => n + 1);
      }, 230); // cascade by 230ms each card
    }
    return () => clearTimeout(timer);
  }, [active, showCards]);

  return (
    <div style={styles.BenefitsParentContainer} ref={sectionRef}>
      <Container maxWidth="lg">
        <Box align="center">
          <Typography
            className="main-heading-font-size"
            variant="h4"
            component="h4"
            color="black"
            sx={{ fontWeight: 500, mb: 2 }}
          >
            Choose Your Ideal Partnership Category
          </Typography> 
        </Box>
        <Grid
          mb={2}
          container
          spacing={2}
          mt={{ xs: "1.3rem", sm: "1.3rem", md: "1.3rem", lg: "1.3rem" }}
          justifyContent="center"
        >
          {BENEFITS_LIST.map((item, index) => (
            <Grid
              item
              key={index}
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <TiltCard
                item={item}
                visible={index < showCards}
                animateDelay={index * 80}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </div>
  );
};

export default DiscoveryCategory;

const styles = {
  BenefitsParentContainer: {
    padding: "2.5rem 1rem",
    // background: "linear-gradient(to right, #6db3f2, #39d2c0)",
    // minHeight: "100vh",
  },
  HeadingTxt: {
    mb: 2,
    color: "black",
    fontFamily: '"Poppins", serif',
    fontWeight: "600",
    fontSize: { xs: "1.35rem", sm: "1.35rem", md: "1.5rem" },
  },
  BenefitsCardContainer: {
    width: { xs: 330, sm: 600, md: 427, lg: 320 },
    minHeight: 140,
    borderRadius: 2,
    boxShadow: "none",
    border: "1px solid #dfc4b6ff",
    padding: 2,
    display: "block",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    transition: "box-shadow 0.18s, transform 0.18s",
    willChange: "transform, box-shadow",
    outline: "none",
  },
  IconContainer: { width: 40, height: 40 },
  LabelTxt: {
    fontWeight: 500,
    fontFamily: '"Poppins", serif',
    fontSize: "0.9rem",
  },
  DescriptionTxt: {
    fontFamily: '"Poppins", serif',
  },
};
